<script>
import { mapState, mapActions } from 'pinia';
import { useAiEnhancerStore } from '@store/useAiEnhancerStore';
import { enhanceContent } from '@resources/js/api/lobbying';
import { AiEnhancementPositions, AiEnhancementTones, AiEnhancementTonesByType, AiEnhancementTypes } from '@resources/js/constants';

export default {
  name: 'CpAiMessageEnhancer',
  directives: {
    marginTop: require('@resources/js/directives/margins').marginTop,
    marginBottom: require('@resources/js/directives/margins').marginBottom,
  },
  components: {
    CpButton: require('@resources/js/button').default,
    CpTooltip: require('@resources/js/tooltip').default,
    CpTooltipToggle: require('@resources/js/tooltip-toggle').default,
  },
  props: {
    message: {
      type: String,
      required: true,
    },
    sourceText: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    minLength: {
      type: Number,
      default: 10,
    },
    position: {
      type: String,
      default: AiEnhancementPositions.FAVOR,
    },
    type: {
      type: String,
      default: AiEnhancementTypes.OFFICIAL,
    },
    buttonText: {
      type: String,
      default: 'Enhance my message',
    },
    showTone: {
      type: Boolean,
      default: true,
    },
    showPosition: {
      type: Boolean,
      default: true,
    },
    infoText: {
      type: String,
      default: 'We use AI to help draft messages. You can edit before sending.',
    },
  },
  data() {
    return {
      AiEnhancementTones,
      AiEnhancementPositions,
      isEnhancing: false,
      enhancedMessage: '',
      showEnhancedPreview: false,
      selectedTone: null,
      selectedPosition: this.position,
      enhancementError: '',
      preEnhancementBody: '',
      reachedLimitMessage: 'You have reached your Enhancement limit for the day',
    };
  },
  mounted() {
    // TO DO: Uncomment this when we implement the daily limit
    // this.checkAndResetDaily();
    if (!this.selectedTone && this.availableTones.length > 0) {
      this.selectedTone = this.availableTones[0].value;
    }
  },
  computed: {
    canEnhance() {
      return !this.disabled && !this.isEnhancing && this.message.trim() && this.message.trim().length >= this.minLength;
    },
    availableTones() {
      return AiEnhancementTonesByType[this.type] || AiEnhancementTonesByType.default;
    },
    // TO DO: Uncomment this when we implement the daily limit
    // ...mapState(useAiEnhancerStore, ['hasReachedLimit', 'canUseEnhancer']),
  },
  methods: {
    async enhanceMessage() {
      // TO DO: Uncomment this when we implement the daily limit
      // this.checkAndResetDaily();

      // TO DO: Uncomment this when we implement the daily limit
      // if (this.hasReachedLimit) {
      //   this.enhancementError = this.reachedLimitMessage;
      //   return;
      // }

      if (!this.message.trim() || this.message.trim().length < this.minLength) {
        this.enhancementError = `Please write at least ${this.minLength} characters before enhancing.`;
        return;
      }

      this.isEnhancing = true;
      this.enhancementError = '';
      this.preEnhancementBody = this.message;

      const requestData = {
        type: this.type,
        message: this.message,
      };

      if (this.sourceText) {
        requestData.source_text = this.sourceText;
      }
      if (this.showTone) {
        requestData.tone = this.selectedTone;
      }
      if (this.showPosition) {
        requestData.position = this.selectedPosition;
      }

      try {
        const response = await enhanceContent(requestData);
        this.enhancedMessage = response.enhanced_message.enhanced_message;
        this.showEnhancedPreview = true;
        // TO DO: Uncomment this when we implement the daily limit
        // this.incrementUsage();
        this.$emit('preview-opened');
      } catch (error) {
        this.enhancementError = error.message || 'Could not enhance message. Please try again.';
        console.warn('Enhancement error:', error);
      } finally {
        this.isEnhancing = false;
      }
    },
    useEnhancedMessage() {
      const htmlMessage = this.enhancedMessage.replace(/\n/g, '<br>');
      this.$emit('message-enhanced', htmlMessage);
      this.closePreview();
    },
    cancelEnhancement() {
      this.closePreview();
    },
    useOriginalMessage() {
      this.$emit('message-enhanced', this.preEnhancementBody);
      this.closePreview();
    },
    closePreview() {
      this.showEnhancedPreview = false;
      this.enhancedMessage = '';
      this.enhancementError = '';
      this.$emit('preview-closed');
    },
    // TO DO: Uncomment this when we implement the daily limit
    // ...mapActions(useAiEnhancerStore, ['incrementUsage', 'checkAndResetDaily']),
  },
};
</script>

<template>
  <div class="ai-enhancement-section" v-margin-top="0.5">
    <div v-if="!showEnhancedPreview" class="enhancement-controls">
      <div v-if="showTone || showPosition" class="enhancement-options">
        <div v-if="showTone" class="enhancement-option">
          <label class="caption1 neutral5-color">Tone:</label>
          <select v-model="selectedTone" class="enhancement-select" :disabled="!canEnhance">
            <option v-for="tone in availableTones" :key="tone.value" :value="tone.value">
              {{ tone.label }}
            </option>
          </select>
        </div>
        <div v-if="showPosition" class="enhancement-option">
          <label class="caption1 neutral5-color">Position:</label>
          <select v-model="selectedPosition" class="enhancement-select" :disabled="!canEnhance">
            <option :value="AiEnhancementPositions.FAVOR">In Favor</option>
            <option :value="AiEnhancementPositions.AGAINST">Against</option>
          </select>
        </div>
      </div>
      <div class="enhancement-button-container">
        <CpButton @click="enhanceMessage" class="enhance-btn" secondary thin :loading="isEnhancing" :disabled="!canEnhance">
          <i class="fas fa-magic" style="margin-right: 5px"></i>
          {{ isEnhancing ? 'Enhancing...' : buttonText }}
        </CpButton>
        <!-- TO DO: Uncomment this when we implement the daily limit -->
        <!-- <CpTooltipToggle v-if="!canUseEnhancer" for="enhancementTooltipId" hoverable class="tooltip-toggle">
          <i class="fal fa-info-circle cursor-pointer error-color"></i>
        </CpTooltipToggle>
        <CpTooltip light unstyled id="enhancementTooltipId">
          <span class="tooltipOption body1 neutral6-color">{{ reachedLimitMessage }}</span>
        </CpTooltip> -->
      </div>
    </div>

    <p v-if="enhancementError" class="footnote error-color" style="margin-top: 5px">{{ enhancementError }}</p>
    <p class="ai-disclaimer caption2 neutral5-color" v-margin-top="0.5" v-margin-bottom="0">
      <i class="fas fa-info-circle"></i>
      {{ infoText }}
    </p>

    <!-- Enhanced Preview -->
    <div v-if="showEnhancedPreview" class="enhanced-preview-container">
      <div class="preview-header">
        <h4 class="subheadline1 black-color">AI-Enhanced Version</h4>
        <span class="caption2 primary-color">Review before using</span>
      </div>

      <div class="preview-comparison">
        <div class="preview-box">
          <h5 class="caption1 neutral5-color">Original:</h5>
          <div class="preview-text original-text" v-html="preEnhancementBody"></div>
        </div>
        <div class="preview-box enhanced-box">
          <h5 class="caption1 neutral5-color">Enhanced:</h5>
          <div class="preview-text enhanced-text">{{ enhancedMessage }}</div>
        </div>
      </div>

      <div class="preview-actions">
        <CpButton @click="useOriginalMessage" thin secondary>Use Original</CpButton>
        <CpButton @click="useEnhancedMessage" thin primary>Use Enhanced</CpButton>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ai-enhancement-section {
  border-top: 1px solid var(--neutral3);
  padding-top: 12px;
  margin-top: 12px;
  background-color: var(--primary-blue-50);
  padding: 8px;
  border-radius: 4px;
}
.enhancement-controls {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}
.enhancement-options {
  display: flex;
  gap: 12px;
}
.enhancement-option {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.enhancement-select {
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: border-color 0.2s;
}
.enhancement-select:hover:not(:disabled) {
  border-color: var(--primary-blue-color);
}
.enhancement-select:disabled {
  opacity: 0.5;
}
.enhance-btn {
  white-space: nowrap;
}
.ai-disclaimer {
  display: flex;
  align-items: center;
  gap: 6px;
  font-style: italic;
}
.enhanced-preview-container {
  border: 2px solid var(--primary-blue-color);
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  background-color: #f8f9ff;
}
.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.preview-comparison {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}
.preview-box {
  flex: 1;
  min-width: 0;
}
.preview-box h5 {
  margin-bottom: 8px;
  font-weight: 600;
}
.preview-text {
  background-color: var(--white);
  border: 1px solid var(--neutral3);
  border-radius: 4px;
  padding: 12px;
  min-height: 80px;
  max-height: 200px;
  overflow-y: auto;
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.5;
}
.original-text {
  color: var(--neutral5);
}
.enhanced-text {
  border-color: var(--primary-blue-color);
  border-width: 2px;
}
.enhanced-box {
  position: relative;
}
.preview-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.enhancement-button-container {
  position: relative;
}
.tooltip-toggle {
  position: absolute;
  bottom: 25px;
  right: 5px;
}
.tooltipOption {
  padding: 8px;
}
@media only screen and (max-width: 768px) {
  .enhancement-controls {
    flex-direction: column;
    align-items: stretch;
  }
  .enhancement-options {
    flex-direction: column;
  }
  .enhance-btn {
    width: 100%;
  }
  .preview-comparison {
    flex-direction: column;
  }
  .preview-actions {
    flex-direction: column;
  }
  .preview-actions button {
    width: 100%;
  }
}
</style>
